import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { HubConnectionBuilder, HubConnectionState, HttpTransportType } from '@microsoft/signalr';
import { signalREventReceived, serverEventReceived } from 'actions/actionCreators/signalRActions';
import { showError, showInfo } from 'actions/actionCreators/snackbarActions';
import { env } from 'utils/env';

let whenSignalRReadyProm;
let outer_access_token;
const accessTokenFactory = () => outer_access_token;

const NotificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [connection, setConnection] = useState();

  const { access_token } = useSelector(state => {
    let access_token;
    if (state.oidc.user && state.oidc.user.access_token) {
      access_token = state.oidc.user.access_token;
    } else {
      if (sessionStorage.getItem('access_token')) {
        access_token = sessionStorage.getItem('access_token');
      }
    }

    return {
      access_token
    };
  }, shallowEqual);

  outer_access_token = access_token;

  const onClosed = useCallback(() => {
    whenSignalRReadyProm = Promise.reject({ message: 'SignalR connection closed' });
    dispatch(showError('SignalR connection closed'));
  }, [dispatch]);

  const onReconnected = useCallback(() => {
    whenSignalRReadyProm = Promise.resolve({ message: 'SignalR reconnected' });
    dispatch(showInfo('SignalR reconnected'));
  }, [dispatch]);

  const onEventReceived = useCallback(
    event => {
      dispatch(signalREventReceived(event));
      if (event.eventType) {
        dispatch(serverEventReceived(event));
      }
    },
    [dispatch]
  );

  const createSignalRConnection = useCallback(() => {
    const con = new HubConnectionBuilder()
      .withUrl(env.REACT_APP_SIGNAL_R_URL + '/events', {
        accessTokenFactory: accessTokenFactory,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets
      })
      .withAutomaticReconnect()
      .build();
    setConnection(con);

    con.onclose(onClosed);
    con.onreconnecting(onClosed);
    con.onreconnected(onReconnected);
    con.on('SendServerEvent', event => {
      onEventReceived(event);
    });
  }, [onClosed, onEventReceived, onReconnected]);

  const tryStartConnection = useCallback(() => {
    if (!connection) return;
    if (connection.state !== HubConnectionState.Disconnected) {
      return whenSignalRReadyProm;
    }

    whenSignalRReadyProm = connection.start().catch(err => {
      throw new Error(err.message || 'SignalR server unreachable');
    });
    return whenSignalRReadyProm;
  }, [connection]);

  useEffect(() => {
    if (!connection && access_token) createSignalRConnection();
    if (access_token) tryStartConnection();
  }, [createSignalRConnection, access_token, tryStartConnection, connection]);

  useEffect(() => {
    return () => {
      connection?.stop();
    };
  }, [connection]);

  return React.Children.only(children);
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function whenSignalRReady() {
  return whenSignalRReadyProm;
}

export default React.memo(NotificationProvider);
